import { supabase } from '../lib/supabaseClient';

// Polyfill for roundRect if not supported
if (!CanvasRenderingContext2D.prototype.roundRect) {
  CanvasRenderingContext2D.prototype.roundRect = function(x: number, y: number, width: number, height: number, radius: number) {
    if (width < 2 * radius) radius = width / 2;
    if (height < 2 * radius) radius = height / 2;
    this.beginPath();
    this.moveTo(x + radius, y);
    this.arcTo(x + width, y, x + width, y + height, radius);
    this.arcTo(x + width, y + height, x, y + height, radius);
    this.arcTo(x, y + height, x, y, radius);
    this.arcTo(x, y, x + width, y, radius);
    this.closePath();
    return this;
  };
}

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  category: string;
  imageUrl?: string;
  isBotCreated?: boolean;
  updated_at: string;
}

interface PromoImageResult {
  '4x5': string;
  '1x1': string;
}

const ASPECT_RATIOS = {
  '4x5': { width: 1080, height: 1350 },
  '1x1': { width: 1080, height: 1080 }
};

const BLUR_RADIUS = 40;
const OVERLAY_OPACITY = 0.6;
const SAFE_MARGIN_PERCENT = 0.06; // 6% safe margins

/**
 * Determines card size based on source image resolution
 */
function getCardSizePercent(sourceWidth: number): number {
  return sourceWidth < 1200 ? 0.65 : 0.80;
}

/**
 * Loads an image from URL and returns dimensions
 */
async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Gets the highest resolution Unsplash image URL
 */
function getHighResUnsplashUrl(url: string): string {
  // If it's an Unsplash image, request high resolution
  if (url.includes('unsplash.com')) {
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?w=2400&q=95&fm=jpg`;
  }
  // For non-Unsplash images, return as-is
  return url;
}

/**
 * Renders blurred background with dark overlay (for 1:1 format)
 */
function renderBackground(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
) {
  // Fill canvas with image (cover fit)
  const imgAspect = img.width / img.height;
  const canvasAspect = canvasWidth / canvasHeight;

  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgAspect > canvasAspect) {
    // Image is wider - fit height
    drawHeight = canvasHeight;
    drawWidth = drawHeight * imgAspect;
    offsetX = (canvasWidth - drawWidth) / 2;
    offsetY = 0;
  } else {
    // Image is taller - fit width
    drawWidth = canvasWidth;
    drawHeight = drawWidth / imgAspect;
    offsetX = 0;
    offsetY = (canvasHeight - drawHeight) / 2;
  }

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

  // Apply blur
  ctx.filter = `blur(${BLUR_RADIUS}px)`;
  ctx.drawImage(ctx.canvas, 0, 0);
  ctx.filter = 'none';

  // Apply dark overlay
  ctx.fillStyle = `rgba(0, 0, 0, ${OVERLAY_OPACITY})`;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
}

/**
 * Renders background for 4:5 format - contains image by height to prevent cropping
 */
function renderBackground4x5(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
) {
  const imgAspect = img.width / img.height;
  const canvasAspect = canvasWidth / canvasHeight;

  // For 4:5 format, prioritize fitting by height (contain) to preserve full subject
  // This prevents cropping of heads, feet, etc.
  let drawWidth, drawHeight, offsetX, offsetY;

  // Fit by height - contains the full image
  drawHeight = canvasHeight;
  drawWidth = drawHeight * imgAspect;
  offsetX = (canvasWidth - drawWidth) / 2;
  offsetY = 0;

  // If image is narrower than canvas, we'll have side gaps
  if (drawWidth < canvasWidth) {
    // Fill gaps with blurred and darkened extensions
    // Draw the image multiple times, blurred, to create side fills
    ctx.save();

    // Left side fill
    ctx.filter = `blur(${BLUR_RADIUS * 1.5}px) brightness(0.3)`;
    ctx.drawImage(img, offsetX - canvasWidth * 0.3, offsetY, canvasWidth * 0.3, drawHeight);

    // Right side fill
    ctx.drawImage(img, offsetX + drawWidth, offsetY, canvasWidth * 0.3, drawHeight);
    ctx.restore();
  }

  // Draw main image (blurred for background)
  ctx.filter = `blur(${BLUR_RADIUS}px)`;
  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  ctx.filter = 'none';

  // Apply dark overlay
  ctx.fillStyle = `rgba(0, 0, 0, ${OVERLAY_OPACITY})`;
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
}

/**
 * Renders the Vybe card exactly as it appears in-app
 */
function renderVybeCard(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  event: Event,
  canvasWidth: number,
  canvasHeight: number,
  cardSizePercent: number
) {
  const cardWidth = canvasWidth * cardSizePercent;
  // Match 4:3 aspect ratio from the app
  let cardHeight = cardWidth * (4 / 3);

  // IMPORTANT: Ensure card fits within canvas with safe margins
  const safeMargin = canvasHeight * 0.08; // 8% top and bottom margins
  const maxCardHeight = canvasHeight - (safeMargin * 2);

  let finalCardWidth = cardWidth;
  if (cardHeight > maxCardHeight) {
    // Scale down to fit by height instead
    cardHeight = maxCardHeight;
    finalCardWidth = cardHeight * (3 / 4); // Maintain 4:3 ratio
  }

  const cardX = (canvasWidth - finalCardWidth) / 2;
  const cardY = (canvasHeight - cardHeight) / 2;

  // App theme colors
  const CARD_BG = '#1A1A40';
  const radius = 16; // rounded-2xl = 1rem

  // Draw card background (matching bg-theme-cards)
  ctx.fillStyle = CARD_BG;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, finalCardWidth, cardHeight, radius);
  ctx.fill();

  // Add card shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
  ctx.shadowBlur = 15;
  ctx.shadowOffsetY = 10;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Draw full event image (cover fit, matching aspect-[4/3])
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, finalCardWidth, cardHeight, radius);
  ctx.clip();

  // Draw image (cover fit)
  const imgAspect = img.width / img.height;
  const cardAspect = finalCardWidth / cardHeight;

  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgAspect > cardAspect) {
    drawHeight = cardHeight;
    drawWidth = drawHeight * imgAspect;
    offsetX = cardX + (finalCardWidth - drawWidth) / 2;
    offsetY = cardY;
  } else {
    drawWidth = finalCardWidth;
    drawHeight = drawWidth / imgAspect;
    offsetX = cardX;
    offsetY = cardY + (cardHeight - drawHeight) / 2;
  }

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

  // Draw bottom gradient overlay (matching the app's gradient)
  const gradientHeight = cardHeight / 3;
  const gradient = ctx.createLinearGradient(
    cardX,
    cardY + cardHeight - gradientHeight,
    cardX,
    cardY + cardHeight
  );
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
  gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.3)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
  ctx.fillStyle = gradient;
  ctx.fillRect(cardX, cardY + cardHeight - gradientHeight, finalCardWidth, gradientHeight);

  ctx.restore();

  // Padding matching the app (p-3 = 12px at standard size)
  const padding = finalCardWidth * 0.028; // Scales with card size

  // Top-right badge (Community/Hosted Vybe) - REMOVED for ad testing
  // const badgeX = cardX + finalCardWidth - padding;
  // const badgeY = cardY + padding;
  // const badgeRadius = 12;
  // const badgePadding = padding * 0.5;
  // const badgeFontSize = Math.floor(finalCardWidth * 0.026);

  // ctx.font = `600 ${badgeFontSize}px Inter, sans-serif`;
  // const badgeIcon = event.isBotCreated ? '✨' : '👤';
  // const badgeText = event.isBotCreated ? 'Community Vybe' : 'Hosted Vybe';
  // const badgeMetrics = ctx.measureText(badgeText);
  // const iconSize = badgeFontSize * 1.1;
  // const badgeWidth = badgeMetrics.width + iconSize + badgePadding * 3.5;
  // const badgeHeight = badgeFontSize + badgePadding * 1.8;

  // // Badge background
  // ctx.fillStyle = event.isBotCreated ? '#ec4899' : '#a855f7';
  // ctx.beginPath();
  // ctx.roundRect(badgeX - badgeWidth, badgeY, badgeWidth, badgeHeight, badgeRadius);
  // ctx.fill();

  // // Badge icon
  // ctx.fillStyle = '#ffffff';
  // ctx.textBaseline = 'middle';
  // ctx.font = `${iconSize}px Inter, sans-serif`;
  // ctx.fillText(badgeIcon, badgeX - badgeWidth + badgePadding * 1.2, badgeY + badgeHeight / 2);

  // // Badge text
  // ctx.font = `600 ${badgeFontSize}px Inter, sans-serif`;
  // ctx.fillText(badgeText, badgeX - badgeWidth + iconSize + badgePadding * 2, badgeY + badgeHeight / 2);

  // Bottom text overlay
  const textX = cardX + padding;
  const textY = cardY + cardHeight - padding;
  const textWidth = finalCardWidth - padding * 2;

  // Title
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${Math.floor(finalCardWidth * 0.038)}px Inter, sans-serif`;
  ctx.textBaseline = 'bottom';

  // Truncate title to one line
  let titleText = event.title;
  let titleMetrics = ctx.measureText(titleText);
  if (titleMetrics.width > textWidth) {
    while (titleMetrics.width > textWidth - 20 && titleText.length > 0) {
      titleText = titleText.slice(0, -1);
      titleMetrics = ctx.measureText(titleText + '...');
    }
    titleText += '...';
  }

  const titleY = textY - padding * 6.5;
  ctx.fillText(titleText, textX, titleY);

  // Metadata container background for visual grouping - adjusted for location only
  const metadataX = textX - padding * 0.8;
  const metadataY = titleY + padding * 0.5;
  const metadataWidth = textWidth + padding * 1.6;
  const metadataHeight = padding * 2.5; // Reduced height since we removed date

  ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
  ctx.beginPath();
  ctx.roundRect(metadataX, metadataY, metadataWidth, metadataHeight, 8);
  ctx.fill();

  // Location with increased visibility (date removed for ads)
  const detailFontSize = Math.floor(finalCardWidth * 0.028); // Increased from 0.024
  ctx.font = `600 ${detailFontSize}px Inter, sans-serif`; // Changed to 600 weight

  // Date removed for ad testing
  // let dateStr = 'Date TBD';
  // if (event.date && event.date.trim() !== '') {
  //   try {
  //     let dateString = event.date.trim();
  //     if (!dateString.includes('T') && !dateString.includes(' ')) {
  //       dateString = dateString + 'T12:00:00Z';
  //     } else if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
  //       dateString = dateString + 'Z';
  //     }
  //     const date = new Date(dateString);
  //     if (!isNaN(date.getTime())) {
  //       const options: Intl.DateTimeFormatOptions = {
  //         weekday: 'short',
  //         year: 'numeric',
  //         month: 'short',
  //         day: 'numeric',
  //         timeZone: 'UTC'
  //       };
  //       dateStr = date.toLocaleDateString('en-GB', options);
  //     }
  //   } catch (e) {
  //     console.error('Date parsing error:', e, 'for date:', event.date);
  //   }
  // }

  // const dateY = metadataY + padding * 1.5;
  // ctx.fillStyle = '#ffffff';
  // ctx.fillText('📅 ' + dateStr, textX, dateY);

  // Capitalize location properly (e.g., "Mote Park" not "mote park")
  const capitalizeLocation = (loc: string): string => {
    return loc.split(' ').map(word => {
      if (word.length === 0) return word;
      // Capitalize first letter, keep rest as-is for acronyms
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  };

  // Truncate location if needed
  let locationText = capitalizeLocation(event.location);
  let locationMetrics = ctx.measureText('📍 ' + locationText);
  if (locationMetrics.width > textWidth) {
    while (locationMetrics.width > textWidth - 20 && locationText.length > 0) {
      locationText = locationText.slice(0, -1);
      locationMetrics = ctx.measureText('📍 ' + locationText + '...');
    }
    locationText += '...';
  }

  // Position location where date used to be (since date is removed)
  const locationY = metadataY + padding * 1.5;
  ctx.fillStyle = '#ffffff'; // Increased contrast to pure white
  ctx.fillText('📍 ' + locationText, textX, locationY);

  // Category badge (matching the app's design) - moved closer to metadata
  const categoryY = locationY + padding * 1.2;
  const categoryFontSize = Math.floor(finalCardWidth * 0.022);
  ctx.font = `500 ${categoryFontSize}px Inter, sans-serif`;

  // Get category icon
  const categoryIcon = getCategoryIcon(event.category);
  const categoryMetrics = ctx.measureText(categoryIcon);
  const categoryPadding = padding * 0.6;
  const categoryBadgeWidth = categoryMetrics.width + categoryPadding * 2;
  const categoryBadgeHeight = categoryFontSize + categoryPadding * 1.5;

  // Category background (light color matching primary-100)
  ctx.fillStyle = 'rgba(15, 15, 41, 0.8)';
  ctx.beginPath();
  ctx.roundRect(textX, categoryY, categoryBadgeWidth, categoryBadgeHeight, 8);
  ctx.fill();

  // Category text
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.fillText(categoryIcon, textX + categoryPadding, categoryY + categoryBadgeHeight / 2);
}

/**
 * Gets category icon (emoji + text)
 */
function getCategoryIcon(category: string): string {
  const categoryMap: Record<string, string> = {
    'Outdoors & Adventures': '🌲',
    'Fitness & Wellness': '💪',
    'Art & Creative Stuff': '🎨',
    'Shows, Movies & Fun': '🎭',
    'Music & Festivals': '🎶',
    'Gaming & Board Games': '🎮',
    'Sports & Events': '🏟️',
    'Learn Something New': '🧠',
    'Meetups & Socials': '🗣️',
    'Volunteering & Good Vybes': '🤝',
    'Fashion & Style': '👗',
    'Tech & Gadgets': '💻',
    'Books & Chill': '📚',
    'Pets & Animals': '🐶',
    'Dance & Movement': '💃',
    'Travel & Trips': '✈️'
  };

  const normalized = category.replace(/[\u{1F300}-\u{1FAFF}]/gu, '').trim();
  const icon = categoryMap[normalized];
  return icon ? `${icon} ${normalized}` : category;
}

/**
 * Generates a promo image for a specific aspect ratio
 */
async function generatePromoImage(
  event: Event,
  aspectRatio: '4x5' | '1x1'
): Promise<Blob> {
  const { width, height } = ASPECT_RATIOS[aspectRatio];

  // Get image URL - use imageUrl which contains the proper CDN URL
  let imageUrl = event.imageUrl;
  if (!imageUrl) {
    throw new Error('No image URL available for event');
  }

  // Request high resolution version
  imageUrl = getHighResUnsplashUrl(imageUrl);

  // Load source image
  const img = await loadImage(imageUrl);

  // Determine card size based on source resolution
  const cardSizePercent = getCardSizePercent(img.width);

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Render background - use 4x5 version for 4:5 aspect ratio to prevent cropping
  if (aspectRatio === '4x5') {
    renderBackground4x5(ctx, img, width, height);
  } else {
    renderBackground(ctx, img, width, height);
  }

  // Render card on top
  renderVybeCard(ctx, img, event, width, height, cardSizePercent);

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Checks if cached images exist and are up to date
 */
async function checkCache(eventId: string, updatedAt: string): Promise<PromoImageResult | null> {
  try {
    // Check if both images exist
    const { data: files, error } = await supabase.storage
      .from('promo-images')
      .list(eventId);

    if (error || !files || files.length !== 2) {
      return null;
    }

    // Get the oldest file's updated time
    const oldestFile = files.reduce((oldest, file) =>
      new Date(file.updated_at) < new Date(oldest.updated_at) ? file : oldest
    );

    // Check if cache is newer than event update
    const cacheTime = new Date(oldestFile.updated_at).getTime();
    const eventTime = new Date(updatedAt).getTime();

    if (cacheTime >= eventTime) {
      // Cache is valid, return URLs
      const { data: { publicUrl: url4x5 } } = supabase.storage
        .from('promo-images')
        .getPublicUrl(`${eventId}/4x5.png`);

      const { data: { publicUrl: url1x1 } } = supabase.storage
        .from('promo-images')
        .getPublicUrl(`${eventId}/1x1.png`);

      return {
        '4x5': url4x5,
        '1x1': url1x1
      };
    }

    return null;
  } catch (error) {
    console.error('Cache check error:', error);
    return null;
  }
}

/**
 * Uploads a generated image to storage
 */
async function uploadImage(
  eventId: string,
  aspectRatio: '4x5' | '1x1',
  blob: Blob
): Promise<string> {
  const path = `${eventId}/${aspectRatio}.png`;

  // Delete existing file if it exists
  await supabase.storage
    .from('promo-images')
    .remove([path]);

  // Upload new file
  const { error } = await supabase.storage
    .from('promo-images')
    .upload(path, blob, {
      contentType: 'image/png',
      cacheControl: '3600',
      upsert: true
    });

  if (error) {
    throw new Error(`Failed to upload ${aspectRatio} image: ${error.message}`);
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from('promo-images')
    .getPublicUrl(path);

  return publicUrl;
}

/**
 * Main function to generate or retrieve promo images
 */
export async function generatePromoImages(event: Event): Promise<PromoImageResult> {
  // Check if generation is already in progress
  const { data: lockCheck } = await supabase
    .from('events')
    .select('promo_generation_in_progress')
    .eq('id', event.id)
    .single();

  if (lockCheck?.promo_generation_in_progress) {
    throw new Error('Promo generation already in progress for this Vybe');
  }

  // Check cache first
  const cached = await checkCache(event.id, event.updated_at);
  if (cached) {
    console.log('Using cached promo images for event', event.id);
    return cached;
  }

  // Set lock to prevent concurrent generations
  const { error: lockError } = await supabase
    .from('events')
    .update({ promo_generation_in_progress: true })
    .eq('id', event.id);

  if (lockError) {
    throw new Error('Failed to acquire generation lock');
  }

  try {
    console.log('Generating new promo images for event', event.id);

    // Generate both aspect ratios
    const [blob4x5, blob1x1] = await Promise.all([
      generatePromoImage(event, '4x5'),
      generatePromoImage(event, '1x1')
    ]);

    // Upload to storage
    const [url4x5, url1x1] = await Promise.all([
      uploadImage(event.id, '4x5', blob4x5),
      uploadImage(event.id, '1x1', blob1x1)
    ]);

    // Also trigger browser downloads
    downloadBlob(blob4x5, `vybe-${event.id}-4x5.png`);
    downloadBlob(blob1x1, `vybe-${event.id}-1x1.png`);

    return {
      '4x5': url4x5,
      '1x1': url1x1
    };
  } finally {
    // Always release the lock, even on error
    await supabase
      .from('events')
      .update({ promo_generation_in_progress: false })
      .eq('id', event.id);
  }
}

/**
 * Helper to trigger browser download
 */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
